import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createVideoPodcast } from '../../api/videoPodcast';
import { useAuth } from '../../components/AuthContext';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import toast from 'react-hot-toast';

const FloatingShape = ({ className }) => (
  <div className={`pointer-events-none absolute rounded-full blur-3xl ${className}`} aria-hidden="true" />
);

const FieldLabel = ({ htmlFor, children, required }) => (
  <label htmlFor={htmlFor} className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-900 dark:text-white max-md:mb-0.5 max-md:text-[10px]">
    {children}
    {required && <span className="ml-0.5 text-red-500">*</span>}
  </label>
);

const inputClass =
  'w-full rounded-xl border border-slate-200/80 bg-white/90 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/20 dark:border-white/10 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500 dark:focus:border-violet-400/60 max-md:px-2.5 max-md:py-1.5 max-md:text-xs';

const selectClass = `${inputClass} appearance-none`;

const CreateVideoPodcastPage = () => {
  const { currentUser, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const videoInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('General');
  const [language, setLanguage] = useState('en');
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isLiveRecording, setIsLiveRecording] = useState(false);

  const categories = ['General', 'Technology', 'Entertainment', 'Sports', 'Business', 'Health', 'Education', 'News', 'Lifestyle', 'Music', 'Comedy', 'Other'];

  const languages = {
    en: 'English',
    hi: 'Hindi',
    kn: 'Kannada',
    te: 'Telugu',
    ta: 'Tamil',
    ml: 'Malayalam',
    mr: 'Marathi',
    bn: 'Bengali',
    gu: 'Gujarati',
    pa: 'Punjabi',
  };

  useEffect(() => {
    if (!currentUser || !token) {
      toast.error('Please login to create video podcasts');
      navigate('/login');
    }
  }, [currentUser, token, navigate]);

  useEffect(() => {
    if (location.state?.recordedVideoFile) {
      const recordedFile = location.state.recordedVideoFile;
      setVideoFile(recordedFile);
      setIsLiveRecording(location.state.isLiveRecording || false);

      if (location.state.title) setTitle(location.state.title);
      if (location.state.description) setDescription(location.state.description);
      if (location.state.category) setCategory(location.state.category);
      if (location.state.language) setLanguage(location.state.language);

      const url = URL.createObjectURL(recordedFile);
      setVideoPreview(url);

      toast.success('Live stream recording loaded! Review and publish your podcast.');
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('video/')) {
        toast.error('Please select a valid video file');
        return;
      }
      if (file.size > 500 * 1024 * 1024) {
        toast.error('Video file size should be less than 500MB');
        return;
      }
      setVideoFile(file);
      setVideoUrl('');
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const handleThumbnailFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setThumbnailPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleVideoUrlChange = (e) => {
    const url = e.target.value.trim();
    setVideoUrl(url);
    if (url) {
      setVideoFile(null);
      setVideoPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentUser || !token) {
      toast.error('Please login to create video podcasts');
      navigate('/login');
      return;
    }

    if (!title.trim()) {
      setError('Title is required');
      toast.error('Title is required');
      return;
    }

    if (!videoFile && !videoUrl.trim()) {
      setError('Please provide either a video file or video URL');
      toast.error('Please provide either a video file or video URL');
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('authorId', currentUser.id);
      formData.append('category', category);
      formData.append('language', language);

      if (isLiveRecording) formData.append('isLiveRecording', 'true');

      if (videoFile) formData.append('video', videoFile);
      else if (videoUrl) formData.append('video', videoUrl);

      if (thumbnailFile) formData.append('thumbnail', thumbnailFile);

      formData.append('isPublished', 'true');

      const result = await createVideoPodcast(formData, token);

      if (result && result.success) {
        toast.success('Video podcast created successfully!');
        if (result.podcast && result.podcast._id) {
          navigate(`/video-podcasts/${result.podcast._id}`);
        } else {
          navigate('/video-podcasts');
        }
      } else {
        throw new Error(result?.message || 'Failed to create video podcast');
      }
    } catch (err) {
      console.error('Error creating video podcast:', err);
      const errorMsg = err.message || 'Failed to create video podcast';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!currentUser || !token) {
    return (
      <div className="app-page flex h-[calc(100dvh-4rem)] items-center justify-center text-app-muted">
        Please login to continue...
      </div>
    );
  }

  return (
    <div className="app-page relative flex h-[calc(100vh-4rem)] max-h-[calc(100vh-4rem)] flex-col overflow-hidden max-md:h-auto max-md:max-h-none max-md:overflow-visible max-md:pb-2">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(139,92,246,0.12),_transparent_50%),radial-gradient(ellipse_at_top_right,_rgba(217,70,239,0.1),_transparent_50%),radial-gradient(ellipse_at_bottom,_rgba(99,102,241,0.08),_transparent_55%)] dark:bg-[radial-gradient(ellipse_at_top_left,_rgba(139,92,246,0.22),_transparent_50%),radial-gradient(ellipse_at_top_right,_rgba(217,70,239,0.18),_transparent_50%),radial-gradient(ellipse_at_bottom,_rgba(99,102,241,0.15),_transparent_55%)]" />
      <FloatingShape className="-left-16 top-10 h-48 w-48 bg-violet-400/20 dark:bg-violet-500/25" />
      <FloatingShape className="right-0 top-1/3 h-56 w-56 bg-fuchsia-400/15 dark:bg-fuchsia-500/20" />

      <div className="relative mx-auto flex h-full min-h-0 w-full max-w-7xl flex-col px-4 pb-3 sm:px-6 sm:pb-4 lg:px-6 xl:max-w-[1400px] max-md:h-auto max-md:px-3 max-md:py-2 max-md:pb-2">
        <div className="relative -mx-4 mb-3 shrink-0 overflow-hidden border-b border-violet-200/60 bg-gradient-to-r from-violet-50/95 via-white/80 to-fuchsia-50/95 px-4 py-2 dark:border-violet-400/20 dark:from-violet-950/60 dark:via-slate-900/50 dark:to-fuchsia-950/60 sm:-mx-6 sm:mb-4 sm:px-6 sm:py-3 lg:-mx-6 max-md:-mx-3 max-md:mb-2 max-md:px-3 max-md:py-1.5">
          <div className="relative flex min-h-[3.75rem] items-center sm:min-h-[4.5rem] max-md:min-h-[2.5rem]">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="absolute left-0 top-1/2 z-10 inline-flex w-fit -translate-y-1/2 items-center gap-1 rounded-full border border-slate-300/80 bg-white/70 px-3 py-1.5 text-sm font-medium text-slate-700 backdrop-blur-md transition hover:border-slate-400 hover:bg-white dark:border-white/15 dark:bg-white/5 dark:text-white/90 dark:hover:border-white/30 dark:hover:bg-white/10 max-md:px-2.5 max-md:py-1 max-md:text-xs"
            >
              <ArrowBackRoundedIcon sx={{ fontSize: 16 }} className="max-md:!text-[14px]" />
              Back
            </button>

            <header className="mx-auto flex w-full max-w-xl flex-col items-center justify-center gap-2 px-14 text-center sm:flex-row sm:gap-4 sm:px-16 max-md:gap-1 max-md:px-11">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 shadow-xl ring-4 ring-violet-100/80 dark:ring-violet-400/20 sm:h-16 sm:w-16 max-md:h-10 max-md:w-10 max-md:rounded-xl max-md:ring-2">
                <PodcastsIcon sx={{ fontSize: 34 }} className="text-white max-md:!text-[26px]" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-2xl lg:text-3xl max-md:text-base max-md:leading-tight">
                  Create Video Podcast
                </h1>
                <p className="mt-0.5 text-xs font-medium text-slate-800 dark:text-slate-300 sm:text-sm max-md:hidden">
                  Share your video content with the community
                </p>
              </div>
            </header>
          </div>
        </div>

        {error && (
          <div className="mb-2 shrink-0 rounded-xl border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800/60 dark:bg-red-950/40">
            <p className="text-xs text-red-600 dark:text-red-400 sm:text-sm">{error}</p>
          </div>
        )}

        {/* Form card */}
        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white/80 shadow-[0_8px_30px_rgba(15,23,42,0.08)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/70 dark:shadow-[0_8px_32px_rgba(0,0,0,0.35)] sm:rounded-3xl max-md:flex-none max-md:overflow-visible max-md:rounded-xl"
        >
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5 lg:overflow-hidden lg:p-6 max-md:overflow-visible max-md:p-2.5">
            <div className="grid h-full grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6 max-md:gap-2">
              {/* Left — details */}
              <div className="flex flex-col gap-3 max-md:gap-2">
                <div>
                  <FieldLabel htmlFor="title" required>Title</FieldLabel>
                  <input
                    id="title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter podcast title"
                    className={inputClass}
                    required
                    maxLength={200}
                  />
                </div>

                <div className="min-h-0 flex-1 lg:flex lg:flex-col max-md:shrink-0">
                  <FieldLabel htmlFor="description">Description</FieldLabel>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe your video podcast..."
                    rows={3}
                    className={`${inputClass} min-h-[72px] resize-none lg:min-h-0 lg:flex-1 max-md:min-h-[2.5rem] max-md:leading-tight`}
                    maxLength={1000}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3 max-md:gap-2">
                  <div>
                    <FieldLabel htmlFor="category">Category</FieldLabel>
                    <select id="category" value={category} onChange={(e) => setCategory(e.target.value)} className={selectClass}>
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <FieldLabel htmlFor="language">Language</FieldLabel>
                    <select id="language" value={language} onChange={(e) => setLanguage(e.target.value)} className={selectClass}>
                      {Object.entries(languages).map(([code, name]) => (
                        <option key={code} value={code}>{name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Right — media */}
              <div className="flex flex-col gap-3 max-md:gap-2">
                <div>
                  <FieldLabel required>Video</FieldLabel>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => videoInputRef.current?.click()}
                      className="group flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-violet-200 bg-violet-50/50 px-3 py-4 text-center transition hover:border-violet-400 hover:bg-violet-50 dark:border-violet-500/30 dark:bg-violet-950/30 dark:hover:border-violet-400/50 dark:hover:bg-violet-950/50 max-md:rounded-lg max-md:px-2 max-md:py-2"
                    >
                      <CloudUploadOutlinedIcon sx={{ fontSize: 28 }} className="mb-1 text-violet-500 transition group-hover:scale-110 max-md:!text-[22px] max-md:mb-0.5" />
                      <span className="text-xs font-bold text-slate-900 dark:text-white max-md:text-[10px]">Upload file</span>
                      <span className="mt-0.5 text-[10px] text-slate-500 dark:text-slate-400 max-md:leading-none">Max 500MB</span>
                      {videoFile && (
                        <span className="mt-1 max-w-full truncate text-[10px] font-medium text-violet-600 dark:text-violet-300 max-md:mt-0.5 max-md:text-[9px]">
                          {videoFile.name}
                        </span>
                      )}
                    </button>
                    <input ref={videoInputRef} id="video" type="file" accept="video/*" onChange={handleVideoFileChange} className="hidden" />

                    <div className="flex flex-col justify-center rounded-xl border border-slate-200/80 bg-slate-50/80 px-3 py-3 dark:border-white/10 dark:bg-white/5 max-md:rounded-lg max-md:px-2 max-md:py-2">
                      <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-slate-900 dark:text-white max-md:mb-1 max-md:gap-1 max-md:text-[10px]">
                        <LinkOutlinedIcon sx={{ fontSize: 16 }} className="text-violet-500 max-md:!text-[14px]" />
                        <span className="max-md:leading-none">Or paste URL</span>
                      </div>
                      <input
                        type="url"
                        value={videoUrl}
                        onChange={handleVideoUrlChange}
                        placeholder="YouTube, Vimeo..."
                        className={`${inputClass} py-1.5 text-xs max-md:py-1 max-md:text-[11px]`}
                      />
                    </div>
                  </div>

                  {videoPreview && (
                    <div className="mt-2 overflow-hidden rounded-xl border border-slate-200/80 dark:border-white/10 max-md:mt-1.5 max-md:rounded-lg">
                      <video src={videoPreview} controls className="max-h-24 w-full bg-black object-contain sm:max-h-28 max-md:max-h-14" />
                    </div>
                  )}
                </div>

                <div>
                  <FieldLabel htmlFor="thumbnail">Thumbnail (optional)</FieldLabel>
                  <button
                    type="button"
                    onClick={() => thumbnailInputRef.current?.click()}
                    className="group flex w-full items-center gap-3 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/50 px-3 py-3 text-left transition hover:border-violet-300 hover:bg-violet-50/30 dark:border-white/10 dark:bg-white/5 dark:hover:border-violet-400/40 dark:hover:bg-violet-950/20 max-md:gap-2 max-md:rounded-lg max-md:px-2 max-md:py-2"
                  >
                    {thumbnailPreview ? (
                      <img src={thumbnailPreview} alt="Thumbnail" className="h-12 w-20 shrink-0 rounded-lg object-cover max-md:h-9 max-md:w-14 max-md:rounded-md" />
                    ) : (
                      <span className="flex h-12 w-20 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-white/10 max-md:h-9 max-md:w-14 max-md:rounded-md">
                        <ImageOutlinedIcon sx={{ fontSize: 24 }} className="text-slate-400 max-md:!text-[20px]" />
                      </span>
                    )}
                    <div className="min-w-0">
                      <span className="block text-xs font-bold text-slate-900 dark:text-white max-md:text-[10px]">
                        {thumbnailFile ? thumbnailFile.name : 'Add cover image'}
                      </span>
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 max-md:leading-none">JPG, PNG or WebP</span>
                    </div>
                  </button>
                  <input ref={thumbnailInputRef} id="thumbnail" type="file" accept="image/*" onChange={handleThumbnailFileChange} className="hidden" />
                </div>
              </div>
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex shrink-0 gap-3 border-t border-slate-200/80 p-4 dark:border-white/10 sm:p-5 max-md:gap-2 max-md:p-2.5 max-md:pb-2">
            <button
              type="button"
              onClick={() => navigate('/freelancer-news')}
              disabled={isSubmitting}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10 max-md:rounded-lg max-md:px-3 max-md:py-2 max-md:text-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-600/25 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 max-md:gap-1.5 max-md:rounded-lg max-md:px-3 max-md:py-2 max-md:text-xs"
            >
              {isSubmitting ? 'Creating...' : 'Create Video Podcast'}
              {!isSubmitting && <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} className="max-md:!text-[16px]" />}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateVideoPodcastPage;
